import { useEffect, useState } from "react";
import { AppShell } from "../app/AppShell";
import { Button } from "../components/Button";
import { Alert, Card, Loading } from "../components/Card";
import { ChoreForm, ChoreList, ChoreRow } from "../components/Chores";
import { PageHead } from "../components/Page";
import { choresApi } from "../lib/chores";
import type { ChoreDraft, ChoreList as ChoreListT } from "../lib/chores";
import { errText } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 관리 › 게시판(#/chores) — 사람이 손으로 할 운영 할 일 ══
   시안 docs/screens/pr-port.md §22 · 결정 docs/decisions/chores.md. 열 때 한 번 읽고, 쓴 뒤에 다시 읽는다 —
   타이머로 박동하지 않는다(에이전트가 남기는 빈도가 하루 몇 건이다). 끝낸 일은 접어 둔다. */

export function ChoresPage() {
  const [data, setData] = useState<ChoreListT | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);

  function load() {
    choresApi
      .list()
      .then((d) => {
        setData(d);
        setLoadErr(null);
      })
      .catch((e) => setLoadErr(errText(e)));
  }
  useEffect(load, []);

  async function create(d: ChoreDraft) {
    setAddBusy(true);
    setAddErr(null);
    try {
      await choresApi.create(d);
      setAdding(false);
      load();
    } catch (e) {
      setAddErr(errText(e));
    } finally {
      setAddBusy(false);
    }
  }

  /** 줄 하나의 쓰기 — 실패 문장은 그 줄 위 한 곳에 선다(서버 문장 그대로). */
  async function rowAct(id: string, f: () => Promise<unknown>, closeEdit = false) {
    setRowBusy(id);
    setRowErr(null);
    try {
      await f();
      if (closeEdit) setEditing(null);
      load();
    } catch (e) {
      setRowErr(errText(e));
    } finally {
      setRowBusy(null);
    }
  }

  return (
    <AppShell route="/chores">
      <PageHead
        title={tx("nav.chores")}
        lead={tx("chores.lead")}
        meta={data ? <span className="caption">{tx("chores.summary", { open: data.open, total: data.total })}</span> : undefined}
        actions={
          <>
            <Button onClick={load}>{tx("common.action.refresh")}</Button>
            {!adding ? (
              <Button variant="primary" onClick={() => setAdding(true)}>
                {tx("chores.add")}
              </Button>
            ) : null}
          </>
        }
      />
      {loadErr ? <Alert tone="error">{tx("chores.loadFailed", { err: loadErr })}</Alert> : null}
      {adding ? (
        <Card title={tx("chores.form.card")}>
          <ChoreForm
            busy={addBusy}
            error={addErr}
            onSubmit={create}
            onCancel={() => {
              setAdding(false);
              setAddErr(null);
            }}
          />
        </Card>
      ) : null}
      {!data && !loadErr ? <Loading text={tx("chores.loading")} /> : null}
      {rowErr ? <Alert tone="error">{rowErr}</Alert> : null}
      {data ? (
        <ChoreList
          rows={data.chores}
          showDone={showDone}
          onShowDone={setShowDone}
          row={(c) => (
            <ChoreRow
              key={c.id}
              c={c}
              busy={rowBusy === c.id}
              editing={editing === c.id}
              onToggle={(done) => void rowAct(c.id, () => choresApi.update(c.id, { done }))}
              onEdit={() => {
                setEditing(c.id);
                setRowErr(null);
              }}
              onSave={(d) => void rowAct(c.id, () => choresApi.update(c.id, d), true)}
              onCancel={() => setEditing(null)}
              onDelete={() => void rowAct(c.id, () => choresApi.remove(c.id), true)}
            />
          )}
        />
      ) : null}
    </AppShell>
  );
}
